/**
 * UsageSparkline — 12-month usage mini-chart for B1 dashboard.
 * Uses Recharts AreaChart. Lazy-loaded to keep B1 initial bundle small.
 * Atlas: B1.
 */
"use client";

import { useMemo } from "react";
import {
  AreaChart,
  Area,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import type { BillRow } from "@petci/fixtures";
import { formatCurrency } from "@/lib/format";

const SPARKLINE_STROKE = "#013A57"; // brand-secondary

interface UsageSparklineProps {
  bills: readonly BillRow[];
}

export function UsageSparkline({ bills }: UsageSparklineProps) {
  const data = useMemo(
    () =>
      [...bills]
        .reverse()
        .slice(-12)
        .map((b) => ({ month: b.month, kWh: b.usageKWh, total: b.total })),
    [bills],
  );

  if (data.length === 0) {
    return (
      <div className="h-16 flex items-center justify-center text-xs text-neutral-500">
        No usage history
      </div>
    );
  }

  return (
    <div className="h-16" role="img" aria-label="12-month usage chart">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="usageGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={SPARKLINE_STROKE} stopOpacity={0.25} />
              <stop offset="95%" stopColor={SPARKLINE_STROKE} stopOpacity={0} />
            </linearGradient>
          </defs>
          <Tooltip
            content={({ payload }) => {
              const d = payload?.[0]?.payload as { month: string; kWh: number; total: number } | undefined;
              if (!d) return null;
              return (
                <div className="bg-white border border-neutral-200 rounded-lg px-2 py-1 text-xs shadow-sm">
                  <p className="font-medium">{d.month}</p>
                  <p className="text-neutral-500">{d.kWh} kWh · {formatCurrency(d.total)}</p>
                </div>
              );
            }}
          />
          <Area
            type="monotone"
            dataKey="kWh"
            stroke={SPARKLINE_STROKE}
            strokeWidth={2}
            fill="url(#usageGrad)"
            dot={false}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
